import React, {Fragment} from 'react';

import {PopoverBody} from 'reactstrap';
import styled from 'styled-components';

import {UserAvatar} from 'app/components/general/avatars';
import {StyledPopoverBottomDIV} from 'app/general/bootstrap/utils';
import {
  type OverlayProps,
  PopoverForm,
  PopoverHeading,
} from 'app/general/overlays';
import {PaginatedList} from 'app/general/widgets/PaginatedList';
import {useSubscribers} from 'app/notifly/api';
import {gt} from 'app/utils/i18n';

import type {SubscriptableType} from 'app/notifly/actions';
import type {SubscribersFetchParamsType} from 'app/notifly/api';
import type {BasicUserSerializedType, ListResponseType} from 'sweet-types';

interface Props {
  objectId: number;
  overlayProps: OverlayProps;
  contentType: SubscriptableType;
}

export function DocumentSubscribersPopover({
  objectId,
  contentType,
  overlayProps,
}: Props) {
  const params: SubscribersFetchParamsType = {
    object_id: objectId,
    content_type: contentType,
  };

  const renderSubscribers = (
    data: ListResponseType<BasicUserSerializedType> | undefined
  ) => {
    if (data == null) {
      return (
        <p className="text-center text-muted py-3 mb-0">
          <i className="fa fa-circle-notch fa-spin mr-2" />
          {gt('Loading subscribers')}...
        </p>
      );
    }

    if (data.count === 0) {
      return (
        <p className="text-muted text-center py-3 mb-0">
          {gt('No one is subscribed yet.')}
        </p>
      );
    }

    return (
      <ul className="list-unstyled mb-0">
        {data.results.map((user) => (
          <li
            key={user.id}
            className="d-flex align-items-center py-2 border-bottom"
          >
            <UserAvatar user={user} small />
            <div className="ml-2 overflow-hidden">
              <div className="text-truncate">{user.name}</div>
              <div className="text-muted small text-truncate">{user.email}</div>
            </div>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <PopoverForm overlayProps={overlayProps}>
      {({close}) => (
        <Fragment>
          <PopoverHeading>{gt('Subscribers')}</PopoverHeading>
          <StyledScrollableContainerDiv className="d-flex flex-column">
            <PopoverBody className="m-0 p-0">
              <p className="text-muted small px-3 pt-3 mb-2">
                {gt(
                  'People who receive notifications for changes to this document.'
                )}
              </p>

              <div className="px-3 pb-2">
                <PaginatedList
                  params={params}
                  hook={useSubscribers}
                  render={renderSubscribers}
                />
              </div>
            </PopoverBody>
          </StyledScrollableContainerDiv>
          <StyledPopoverBottomDIV>
            <button onClick={close} className="btn btn-link btn-sm">
              <i className="fa fa-times" /> {gt('Close')}
            </button>
          </StyledPopoverBottomDIV>
        </Fragment>
      )}
    </PopoverForm>
  );
}

const StyledScrollableContainerDiv = styled.div`
  max-height: calc(100vh - 300px);
  width: 274px;
  overflow: auto;
`;
